import SEO from '../components/SEO';
import React from 'react';
import Navigation from '../components/Navigation';
import SiteFooter from '../components/SiteFooter';

export default function GetStartedPage() {
  const terms = [
    {
      number: 1,
      term: "Premium",
      definition: "The amount you pay—usually each month—for your health insurance coverage. Premiums are often automatically deducted from your paycheck.",
      proTip: "Premiums are fixed regardless of doctor visits. Compare total costs (premium + deductible) when choosing plans."
    },
    {
      number: 2,
      term: "Deductible",
      definition: "The amount you pay out of pocket for covered services before your insurance plan starts to contribute.",
      proTip: "Check if preventive care counts toward your deductible—most plans cover it 100% regardless."
    },
    {
      number: 3,
      term: "Copayment (Copay)",
      definition: "A flat dollar amount you pay when you receive certain medical services.",
      proTip: "Copays often apply before deductible. Ask your doctor's office which applies to your visit."
    },
    {
      number: 4,
      term: "Coinsurance",
      definition: "Your share of the cost of a covered service (as a percentage) after your deductible is met.",
      proTip: "After deductible, coinsurance kicks in. Track spending to avoid surprises on big procedures."
    },
    {
      number: 5,
      term: "Out-of-Pocket Maximum",
      definition: "The most you'll pay in a plan year for covered healthcare costs. After reaching this limit, your plan covers 100%.",
      proTip: "Includes deductibles/copays/coinsurance but not premiums. Great families hit this with multiple claims."
    },
    {
      number: 6,
      term: "Embedded vs. Non-Embedded Deductible",
      definition: "Embedded: Individual deductibles per family member. Non-Embedded: Family shares one total.",
      proTip: "Embedded is better if one family member has high medical costs—coverage starts sooner for them."
    },
    {
      number: 7,
      term: "In-Network vs. Out-of-Network Providers",
      definition: "In-Network: Contracted providers = lower costs. Out-of-Network: Higher costs, limited coverage.",
      proTip: "Always verify providers are in-network before appointments. Use your insurance app's search tool."
    },
    {
      number: 8,
      term: "Explanation of Benefits (EOB)",
      definition: "Insurance summary showing what was billed, paid, and your balance (not a bill).",
      proTip: "Review EOBs monthly. Errors happen—catch billing mistakes before they become bills."
    },
    {
      number: 9,
      term: "Preventive Care",
      definition: "Routine services covered 100% in-network (annual exams, vaccines, screenings).",
      proTip: "Schedule your free annual physical. It's the only service often covered before deductible."
    },
    {
      number: 10,
      term: "Formulary",
      definition: "List of covered prescription drugs organized by cost tiers.",
      proTip: "Ask your doctor/pharmacist for formulary (Tier 1) alternatives before getting expensive scripts."
    },
    {
      number: 11,
      term: "HMO (Health Maintenance Organization)",
      definition: "Requires primary doctor and referrals; lowest premiums.",
      proTip: "Great for routine care. Get referrals early—specialist visits denied without them."
    },
    {
      number: 12,
      term: "PPO (Preferred Provider Organization)",
      definition: "Flexible—no referrals needed, out-of-network OK (but costlier).",
      proTip: "Most popular plan type. Balance of flexibility and cost control."
    },
    {
      number: 13,
      term: "FSA (Flexible Spending Account)",
      definition: "Use it or lose it - pre-tax money for healthcare, forfeited if unused by year-end.",
      proTip: "Estimate conservatively. Common uses: contacts, LASIK, braces, dentist bills."
    },
    {
      number: 14,
      term: "HSA (Health Savings Account)",
      definition: "Funds roll over year-to-year—tax-free savings for HDHP members.",
      proTip: "Max contributions ($4,300 individual/$8,550 family in 2026). Invest unused funds—they grow tax-free."
    },
    {
      number: 15,
      term: "HDHP (High Deductible Health Plan)",
      definition: "Lower premiums, higher deductibles—pairs with HSA.",
      proTip: "Best for healthy people who save in HSA. Risky if you expect frequent medical care."
    },
    {
      number: 16,
      term: "HRA (Health Reimbursement Arrangement)",
      definition: "Employer-funded account reimburses medical expenses tax-free.",
      proTip: "Ask HR what qualifies. Often covers premiums, deductibles, even some OTC items."
    },
    {
      number: 17,
      term: "LSA (Lifestyle Spending Account)",
      definition: "Employer allowance for wellness/gym/ergonomics (taxable benefit).",
      proTip: "Common uses: standing desks, fitness trackers, massage therapy. Check employer list."
    },
    {
      number: 18,
      term: "EPO (Exclusive Provider Organization)",
      definition: "Like HMO, no referrals—but no out-of-network coverage.",
      proTip: "Verify all specialists in-network before emergencies. Least flexible plan type."
    },
    {
      number: 19,
      term: "Prior Authorization",
      definition: "Insurance approval required before certain treatments covered.",
      proTip: "Doctor's office usually handles this. Ask about delays before scheduling MRIs/specialists."
    },
    {
      number: 20,
      term: "Open Enrollment",
      definition: "Annual window (usually fall) to change/add benefits.",
      proTip: "Mark your calendar. Life changes (marriage, birth) allow changes anytime."
    },
    {
      number: 21,
      term: "Appeal",
      definition: "Challenge denied claims/coverage decisions.",
      proTip: "60-180 day window typically. Keep all records/EOBs. 50%+ success rate."
    },
    {
      number: 22,
      term: "Primary Care Physician (PCP)",
      definition: "Your main doctor for routine care, checkups, and referrals to specialists (required in HMO plans).",
      proTip: "Choose one you trust—they coordinate ALL your care and know your medical history."
    },
    {
      number: 23,
      term: "Secondary Care Provider",
      definition: "Specialists (cardiologists, dermatologists, etc.) you see for specific conditions, usually referred by your PCP.",
      proTip: "Confirm specialist is in-network AND accepts your PCP's referral before appointment."
    },
    {
      number: 24,
      term: "Care Level Cost Comparison",
      definition: "Understanding when to use Primary Care ($30-50 copay), Urgent Care ($100-200), or Emergency Room ($500-3,000+).",
      proTip: "ER = Emergency Only. Sore throats, sprains, UTIs? Use Urgent Care. Routine issues? See PCP. Save $1,000+ per visit!"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Get Started - Free Benefits Analysis" description="Start maximizing your employee benefits today. Free tools to compare job offers, calculate 401k matching, evaluate health insurance, and understand your total compensation." path="/getstarted" keywords="compare job offers benefits, 401k contribution calculator, health insurance comparison, benefits package evaluation, what questions to ask about benefits in interview" />
      
      <Navigation />
      
      {/* Hero Section - matches other pages */}
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Health Insurance Terms & Definitions</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
            Master the language of health insurance with pro tips for smart usage
          </p>
        </div>
      </section>

      {/* Terms Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {terms.map((item) => (
              <div key={item.number} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold">
                      {item.number}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.term}</h3>
                    <p className="text-gray-700 mb-3">{item.definition}</p>
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-3 rounded">
                      <p className="text-sm text-blue-900">
                        <span className="font-semibold">💡 Pro Tip:</span> {item.proTip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison Chart */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">When to Use Which Care Level</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 border-2 border-green-600 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">🏥</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Primary Care</h3>
              <div className="text-3xl font-bold text-green-600 mb-3">$30-50</div>
              <p className="text-sm text-gray-700">Routine checkups, prescriptions, minor illnesses</p>
            </div>
            <div className="bg-yellow-50 border-2 border-yellow-600 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Urgent Care</h3>
              <div className="text-3xl font-bold text-yellow-600 mb-3">$100-200</div>
              <p className="text-sm text-gray-700">Sprains, UTIs, sore throats, minor burns</p>
            </div>
            <div className="bg-red-50 border-2 border-red-600 rounded-xl p-6 text-center">
              <div className="text-4xl mb-3">🚨</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Emergency Room</h3>
              <div className="text-3xl font-bold text-red-600 mb-3">$500-3,000+</div>
              <p className="text-sm text-gray-700">Life-threatening emergencies only</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-hero text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Maximize Your Benefits?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Explore our articles and tools to save thousands on healthcare costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/articles" className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-all">
              Read Articles
            </a>
            <a href="/contact" className="inline-block bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-xl font-bold text-lg transition-all">
              Get Expert Help
            </a>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
